/**
 * Message generator for product history notes
 *
 * Generates human-readable messages from structured data (eventType + metadata + newValue/previousValue)
 * This avoids storing redundant message strings in the database.
 */

import { ProductHistoryDetailResponseDto } from '../api/models/ProductHistoryDetailResponseDto';
import { ProductHistoryListResponseDto } from '../api/models/ProductHistoryListResponseDto';

interface NotesData {
  template?: string;
  metadata?: Record<string, any>;
}

/**
 * Generate human-readable message from history entry
 * Uses eventType + newValue/previousValue + notes metadata to build the message
 *
 * @param history - History entry with eventType, newValue, previousValue, and notes
 * @returns Human-readable message string
 */
export function generateHistoryMessage(
  history: ProductHistoryListResponseDto | ProductHistoryDetailResponseDto,
): string | undefined {
  const eventType = history.eventType;
  // Only ProductHistoryDetailResponseDto has newValue, previousValue, and notes
  const detailHistory = history as ProductHistoryDetailResponseDto;
  const newValue = detailHistory.newValue as Record<string, any> | undefined;
  const previousValue = detailHistory.previousValue as Record<string, any> | undefined;
  const rawNotes = detailHistory.notes as NotesData | string | undefined;
  let notes = rawNotes as NotesData | undefined;

  // Handle notes serialized as JSON string
  if (typeof rawNotes === 'string') {
    try {
      const parsed = JSON.parse(rawNotes);
      if (parsed && typeof parsed === 'object') {
        notes = parsed as NotesData;
      } else {
        return rawNotes;
      }
    } catch {
      return rawNotes;
    }
  }

  // Handle legacy notes with message field (shouldn't happen in new data)
  if (notes && typeof notes === 'object' && 'message' in notes) {
    return (notes as any).message;
  }

  // Generate message from eventType + data
  switch (eventType) {
    case ProductHistoryDetailResponseDto.eventType.PROPERTY_ADDED:
      return generatePropertyAddedMessage(newValue, notes);

    case ProductHistoryDetailResponseDto.eventType.PROPERTY_UPDATED:
      return generatePropertyUpdatedMessage(newValue, previousValue, notes);

    case ProductHistoryDetailResponseDto.eventType.PROPERTY_DELETED:
      return generatePropertyDeletedMessage(previousValue, notes);

    case ProductHistoryDetailResponseDto.eventType.PRODUCT_CREATED:
      return generateProductCreatedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.PRODUCT_DELETED:
      return generateProductDeletedMessage(previousValue);

    case ProductHistoryDetailResponseDto.eventType.INVENTORY_RECORD_CREATED:
      return generateInventoryRecordCreatedMessage(newValue, history.recordCode);

    case ProductHistoryDetailResponseDto.eventType.INVENTORY_RECORD_COMPLETED:
      return generateInventoryRecordCompletedMessage(newValue, history.recordCode);

    case ProductHistoryDetailResponseDto.eventType.INVENTORY_RECORD_UPDATED:
      return generateInventoryRecordUpdatedMessage(newValue, history.recordCode);

    case ProductHistoryDetailResponseDto.eventType.INVENTORY_RECORD_CANCELLED:
      return generateInventoryRecordCancelledMessage(history.recordCode);

    case ProductHistoryDetailResponseDto.eventType.INVENTORY_RECORD_DELETED:
      return generateInventoryRecordDeletedMessage(history.recordCode);

    case ProductHistoryDetailResponseDto.eventType.PAYMENT_RECEIVED:
      return generatePaymentReceivedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.ORDER_REFUNDED:
      return generateOrderRefundedMessage(newValue, previousValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.ORDER_PARTIALLY_REFUNDED:
      return generateOrderPartiallyRefundedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.ITEM_RETURNED:
      return generateItemReturnedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.REFUND_PROCESSED:
      return generateRefundProcessedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.EXCHANGE_PROCESSED:
      return generateExchangeProcessedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.ORDER_DELIVERY_UPDATED:
      return generateOrderDeliveryUpdatedMessage(newValue, history.orderCode);

    case ProductHistoryDetailResponseDto.eventType.TRANSFER_CREATED:
      return generateTransferCreatedMessage(newValue, history.transferCode);

    case ProductHistoryDetailResponseDto.eventType.TRANSFER_COMPLETED:
      return generateTransferCompletedMessage(newValue, history.transferCode);

    case ProductHistoryDetailResponseDto.eventType.TRANSFER_CANCELLED:
      return generateTransferCancelledMessage(history.transferCode);

    case ProductHistoryDetailResponseDto.eventType.REQUEST_CREATED:
      return generateRequestCreatedMessage(newValue, history.transferCode);

    case ProductHistoryDetailResponseDto.eventType.REQUEST_RESOLVED:
      return generateRequestResolvedMessage(newValue, history.transferCode);

    case ProductHistoryDetailResponseDto.eventType.RESERVATION_EXPIRED:
      return generateReservationExpiredMessage(newValue, notes);

    case ProductHistoryDetailResponseDto.eventType.SAFETY_STOCK_UPDATED_INDIVIDUAL:
      return generateSafetyStockIndividualMessage(previousValue, newValue);

    case ProductHistoryDetailResponseDto.eventType.SAFETY_STOCK_UPDATED_BULK:
      return generateSafetyStockBulkMessage(newValue, notes);

    case ProductHistoryDetailResponseDto.eventType.PHYSICAL_COUNT_FINALIZED:
      return generatePhysicalCountFinalizedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.ROLE_CREATED:
      return generateRoleCreatedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.ROLE_UPDATED:
      return generateRoleUpdatedMessage(previousValue, newValue);

    case ProductHistoryDetailResponseDto.eventType.ROLE_DELETED:
      return generateRoleDeletedMessage(previousValue);

    case ProductHistoryDetailResponseDto.eventType.ROLE_PERMISSIONS_UPDATED:
      return generateRolePermissionsUpdatedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.USER_CREATED:
      return generateUserCreatedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.USER_UPDATED:
      return generateUserUpdatedMessage(previousValue, newValue);

    case ProductHistoryDetailResponseDto.eventType.USER_DELETED:
      return generateUserDeletedMessage(previousValue);

    case ProductHistoryDetailResponseDto.eventType.USER_ROLES_UPDATED:
      return generateUserRolesUpdatedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.LOGIN_SUCCESS:
      return generateLoginSuccessMessage();

    case ProductHistoryDetailResponseDto.eventType.LOGIN_FAILED:
      return generateLoginFailedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.ACCOUNT_LOCKED:
      return generateAccountLockedMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.ACCOUNT_UNLOCKED:
      return generateAccountUnlockedMessage();

    case ProductHistoryDetailResponseDto.eventType.PASSWORD_CHANGED:
      return generatePasswordChangedMessage();

    case ProductHistoryDetailResponseDto.eventType.PASSWORD_RESET_BY_ADMIN:
      return generatePasswordResetByAdminMessage(newValue);

    case ProductHistoryDetailResponseDto.eventType.FORCED_PASSWORD_CHANGE:
      return generateForcedPasswordChangeMessage();

    case ProductHistoryDetailResponseDto.eventType.AVAILABILITY_STATUS_CHANGED:
      // Check if this is a reservation expiration
      if (notes?.metadata?.autoExpired || newValue?.reserved_by === null) {
        return generateReservationExpiredMessage(newValue, notes);
      }
      return undefined;

    default:
      // Fallback: try to extract from notes metadata (legacy)
      if (notes && typeof notes === 'object') {
        const metadata = (notes as NotesData).metadata;
        if (metadata?.legacyMessage) {
          return metadata.legacyMessage;
        }
      }
      return undefined;
  }
}

// Property event message generators
function generatePropertyAddedMessage(
  newValue: Record<string, any> | undefined,
  notes: NotesData | undefined,
): string | undefined {
  if (!newValue) return undefined;

  const propertyType = newValue.propertyType;
  const propertyName = newValue.propertyName;
  const propertyCode = newValue.propertyCode;
  const source = newValue.source ?? notes?.metadata?.source ?? '';

  if (!propertyType || !propertyName) return undefined;

  const sourceText = source === 'SETTINGS' ? 'desde Ajustes' : '';
  const codeText = propertyCode ? ` (${propertyCode})` : '';

  const propertyTypeLabel = getPropertyTypeLabel(propertyType);
  return `${propertyTypeLabel} agregad${propertyType === 'CATEGORY' ? 'a' : 'o'} ${sourceText}: ${propertyName}${codeText}`;
}

function generatePropertyUpdatedMessage(
  newValue: Record<string, any> | undefined,
  previousValue: Record<string, any> | undefined,
  notes: NotesData | undefined,
): string | undefined {
  if (!newValue) return undefined;

  const propertyType = newValue.propertyType;
  const propertyName = newValue.propertyName;
  const propertyCode = newValue.propertyCode;
  const source = newValue.source ?? notes?.metadata?.source ?? previousValue?.source ?? '';

  if (!propertyType || !propertyName) return undefined;

  const sourceText =
    source === 'SETTINGS'
      ? 'desde Ajustes'
      : source === 'WAREHOUSE'
        ? 'desde Gestión en Almacén'
        : source === 'CATALOG'
          ? 'desde Catálogo'
          : '';
  const codeText = propertyCode ? ` (${propertyCode})` : '';

  const propertyTypeLabel = getPropertyTypeLabel(propertyType);
  return `${propertyTypeLabel} actualizad${propertyType === 'CATEGORY' ? 'a' : 'o'} ${sourceText}: ${propertyName}${codeText}`;
}

function generatePropertyDeletedMessage(
  previousValue: Record<string, any> | undefined,
  notes: NotesData | undefined,
): string | undefined {
  if (!previousValue) return undefined;

  const propertyType = previousValue.propertyType;
  const propertyName = previousValue.propertyName;
  const propertyCode = previousValue.propertyCode;
  const source = previousValue.source ?? notes?.metadata?.source ?? '';

  if (!propertyType || !propertyName) return undefined;

  const sourceText = source === 'SETTINGS' ? 'desde Ajustes' : '';
  const codeText = propertyCode ? ` (${propertyCode})` : '';

  const propertyTypeLabel = getPropertyTypeLabel(propertyType);
  return `${propertyTypeLabel} eliminad${propertyType === 'CATEGORY' ? 'a' : 'o'} ${sourceText}: ${propertyName}${codeText}`;
}

function getPropertyTypeLabel(propertyType: string): string {
  const labels: Record<string, string> = {
    CATEGORY: 'Categoría',
    MATERIAL: 'Material',
    SUPPLIER: 'Proveedor',
    TAG: 'Etiqueta',
    PAYMENT_METHOD: 'Método de pago',
  };
  return labels[propertyType] ?? propertyType;
}

// Product event message generators
function generateProductCreatedMessage(
  newValue: Record<string, any> | undefined,
): string | undefined {
  if (!newValue) return undefined;
  const code = newValue.code;
  const name = newValue.name;
  if (code && name) {
    return `Producto creado: ${code} - ${name}`;
  }
  return code ? `Producto creado: ${code}` : name ? `Producto creado: ${name}` : undefined;
}

function generateProductDeletedMessage(
  previousValue: Record<string, any> | undefined,
): string | undefined {
  if (!previousValue) return undefined;
  const code = previousValue.code;
  const name = previousValue.name;
  if (code && name) {
    return `Producto eliminado: ${code} - ${name}`;
  }
  return code ? `Producto eliminado: ${code}` : name ? `Producto eliminado: ${name}` : undefined;
}

// Inventory record message generators
function generateInventoryRecordCreatedMessage(
  newValue: Record<string, any> | undefined,
  recordCode?: string,
): string | undefined {
  const code = recordCode ?? newValue?.recordCode;
  if (!code) return undefined;
  const status = newValue?.status;
  return status === 'COMPLETED'
    ? `Registro de inventario completado: ${code}`
    : `Registro de inventario creado (borrador): ${code}`;
}

function generateInventoryRecordCompletedMessage(
  newValue: Record<string, any> | undefined,
  recordCode?: string,
): string | undefined {
  const code = recordCode ?? newValue?.recordCode;
  const products = newValue?.products;
  const productCount = Array.isArray(products)
    ? products.length
    : typeof newValue?.warehouseProductsCreated === 'number'
      ? newValue.warehouseProductsCreated
      : 0;
  if (!code) return undefined;
  if (productCount > 0) {
    return `Registro de inventario completado: ${code} — ${productCount} pieza${productCount === 1 ? '' : 's'} registrada${productCount === 1 ? '' : 's'}`;
  }
  return `Registro de inventario completado: ${code}`;
}

function generateInventoryRecordUpdatedMessage(
  newValue: Record<string, any> | undefined,
  recordCode?: string,
): string | undefined {
  const code = recordCode ?? newValue?.recordCode;
  return code ? `Registro de inventario actualizado: ${code}` : undefined;
}

function generateInventoryRecordCancelledMessage(recordCode?: string): string | undefined {
  return recordCode ? `Registro de inventario cancelado: ${recordCode}` : undefined;
}

function generateInventoryRecordDeletedMessage(recordCode?: string): string | undefined {
  return recordCode ? `Registro de inventario eliminado: ${recordCode}` : undefined;
}

// Payment/Order message generators
function generatePaymentReceivedMessage(
  newValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  if (!newValue && !orderCode) return undefined;
  const amount = newValue?.paymentAmount;
  const method = newValue?.paymentMethod;
  const code = orderCode ?? newValue?.orderCode;
  if (code && amount && method) {
    return `Pago procesado: ${code} - ${amount} Bs (${method})`;
  }
  return code ? `Pago procesado: ${code}` : undefined;
}

export interface RefundHistoryProductEntry {
  productCode?: string;
  productCodeUnique?: string;
  productName?: string;
  refundAmount?: number;
  returnReason?: string;
}

function formatRefundProductsMessagePart(
  products: RefundHistoryProductEntry[] | undefined,
  maxShown = 3,
): string {
  if (!products?.length) return '';

  const label = (p: RefundHistoryProductEntry) => {
    const code = p.productCodeUnique?.trim() || p.productCode?.trim() || '—';
    const name = p.productName?.trim();
    return name ? `${code} (${name})` : code;
  };

  if (products.length <= maxShown) {
    return products.map(label).join('; ');
  }

  const shown = products.slice(0, maxShown).map(label).join('; ');
  return `${shown} (+ ${products.length - maxShown} más)`;
}

function generateOrderRefundedMessage(
  newValue: Record<string, any> | undefined,
  previousValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  const code = orderCode ?? newValue?.orderCode ?? previousValue?.orderCode;
  const amount = newValue?.refundAmount ?? previousValue?.refundAmount;
  const refundProducts = newValue?.products;
  const productsPart = formatRefundProductsMessagePart(
    Array.isArray(refundProducts) ? refundProducts : undefined,
  );
  if (code && amount != null) {
    const pieces = productsPart ? ` Piezas: ${productsPart}.` : '';
    return `Venta reembolsada: ${code} - Monto: ${amount} Bs.${pieces}`;
  }
  return code ? `Venta reembolsada: ${code}` : undefined;
}

function generateOrderPartiallyRefundedMessage(
  newValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  const code = orderCode ?? newValue?.orderCode;
  const itemCount = newValue?.itemsReturned ?? newValue?.itemCount;
  const amount = newValue?.refundAmount;
  const refundProducts = newValue?.products;
  const productsPart = formatRefundProductsMessagePart(
    Array.isArray(refundProducts) ? refundProducts : undefined,
  );
  if (code && itemCount != null && amount != null) {
    const pieces = productsPart ? ` Piezas: ${productsPart}.` : '';
    return `Reembolso parcial: ${code} - ${itemCount} pieza${itemCount === 1 ? '' : 's'} - Monto: ${amount} Bs.${pieces}`;
  }
  return code ? `Reembolso parcial: ${code}` : undefined;
}

function generateItemReturnedMessage(
  newValue: Record<string, any> | undefined,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  orderCode?: string,
): string | undefined {
  const productCode = newValue?.productCodeUnique;
  const reason = newValue?.returnReason;
  if (productCode && reason) {
    return `Producto devuelto: ${productCode} - Razón: ${reason}`;
  }
  return productCode ? `Producto devuelto: ${productCode}` : undefined;
}

// Transfer message generators
function generateTransferCreatedMessage(
  newValue: Record<string, any> | undefined,
  transferCode?: string,
): string | undefined {
  const code = transferCode ?? newValue?.transferCode;
  return code ? `Transferencia creada: ${code}` : undefined;
}

function generateTransferCompletedMessage(
  newValue: Record<string, any> | undefined,
  transferCode?: string,
): string | undefined {
  const code = transferCode ?? newValue?.transferCode;
  return code ? `Transferencia completada: ${code}` : undefined;
}

function generateTransferCancelledMessage(transferCode?: string): string | undefined {
  return transferCode ? `Transferencia cancelada: ${transferCode}` : undefined;
}

function generateRequestCreatedMessage(
  newValue: Record<string, any> | undefined,
  requestCode?: string,
): string | undefined {
  const code = requestCode ?? newValue?.requestCode;
  const count = newValue?.totalProducts;
  if (code && count != null) {
    return `Solicitud creada: ${code} (${count} pieza${Number(count) === 1 ? '' : 's'})`;
  }
  return code ? `Solicitud creada: ${code}` : undefined;
}

function generateRequestResolvedMessage(
  newValue: Record<string, any> | undefined,
  requestCode?: string,
): string | undefined {
  const code = requestCode ?? newValue?.requestCode;
  const approved = newValue?.approvedCount;
  const rejected = newValue?.rejectedCount;
  const transferCode = newValue?.transferCode;
  if (code && approved != null && rejected != null) {
    const trlPart = transferCode ? ` · TRL ${transferCode}` : '';
    return `Solicitud resuelta: ${code} (${approved} aprobada(s), ${rejected} rechazada(s)${trlPart})`;
  }
  return code ? `Solicitud resuelta: ${code}` : undefined;
}

// Refund message generators
function generateRefundProcessedMessage(
  newValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  const code = orderCode ?? newValue?.orderCode;
  const amount = newValue?.refundAmount;
  const isPartial = newValue?.exchangeType === undefined; // If it's not an exchange, it might be partial
  if (code && amount != null) {
    return `Reembolso procesado${isPartial ? ' (parcial)' : ''}: ${code} - ${amount} Bs`;
  }
  return code ? `Reembolso procesado: ${code}` : undefined;
}

// Exchange message generators
function generateExchangeProcessedMessage(
  newValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  const code = orderCode ?? newValue?.orderCode;
  const exchangeType = newValue?.exchangeType;
  const productName = newValue?.productName ?? newValue?.product?.name;

  if (!code) return undefined;

  if (exchangeType === 'EXCHANGED_OUT') {
    return productName
      ? `Intercambio procesado (salida): ${code} - ${productName}`
      : `Intercambio procesado (salida): ${code}`;
  } else if (exchangeType === 'EXCHANGED_IN') {
    return productName
      ? `Intercambio procesado (entrada): ${code} - ${productName}`
      : `Intercambio procesado (entrada): ${code}`;
  }

  return `Intercambio procesado: ${code}`;
}

function generateOrderDeliveryUpdatedMessage(
  newValue: Record<string, any> | undefined,
  orderCode?: string,
): string | undefined {
  const code = orderCode ?? newValue?.orderCode;
  const delivered = typeof newValue?.deliveredCount === 'number' ? newValue.deliveredCount : 0;
  const undelivered =
    typeof newValue?.undeliveredCount === 'number' ? newValue.undeliveredCount : 0;
  const deliveryStatus =
    typeof newValue?.deliveryStatus === 'string' ? newValue.deliveryStatus : '';

  const parts: string[] = [];
  if (delivered > 0) {
    parts.push(
      `${delivered} pieza${delivered === 1 ? '' : 's'} entregada${delivered === 1 ? '' : 's'} al cliente`,
    );
  }
  if (undelivered > 0) {
    parts.push(`${undelivered} pieza${undelivered === 1 ? '' : 's'} con entrega revertida`);
  }
  const detail = parts.length > 0 ? parts.join('; ') : 'entrega al cliente actualizada';
  const statusSuffix = deliveryStatus ? ` Estado de entrega: ${deliveryStatus}.` : '';
  return code
    ? `Entrega actualizada en ${code}: ${detail}.${statusSuffix}`
    : `Entrega actualizada: ${detail}.${statusSuffix}`;
}

function generateSafetyStockIndividualMessage(
  previousValue: Record<string, any> | undefined,
  newValue: Record<string, any> | undefined,
): string | undefined {
  const code = newValue?.productCode ?? previousValue?.productCode;
  const name = newValue?.productName ?? previousValue?.productName;
  const branch = newValue?.locationName ?? previousValue?.locationName;
  const prev = previousValue?.safetyStock;
  const next = newValue?.safetyStock;
  const prevStr = prev == null ? 'sin configurar' : String(prev);
  const nextStr = next == null ? 'sin configurar' : String(next);
  const productLabel = code && name ? `${code} — ${name}` : code || name || 'Producto';
  const branchSuffix = branch ? ` · ${branch}` : '';
  return `Stock de seguridad (edición por producto): ${productLabel}${branchSuffix}. Valor: ${prevStr} → ${nextStr}.`;
}

export interface SafetyStockSelectionItemDetail {
  productCode?: string;
  productName?: string;
  locationName?: string;
  previousSafetyStock?: number | null;
  newSafetyStock?: number | null;
}

/** Resumen legible de productos seleccionados en stock de seguridad. */
export function formatSafetyStockSelectionItemsSummary(
  details: SafetyStockSelectionItemDetail[],
  maxShown = 3,
): string {
  if (!details.length) return '';

  const formatLine = (d: SafetyStockSelectionItemDetail) => {
    const code = d.productCode?.trim() || '—';
    const name = d.productName?.trim() || 'Producto';
    const loc = d.locationName?.trim() || 'Ubicación';
    return `${code} — ${name} · ${loc}`;
  };

  if (details.length <= maxShown) {
    return details.map(formatLine).join('; ');
  }

  const shown = details.slice(0, maxShown).map(formatLine).join('; ');
  return `${shown} (+ ${details.length - maxShown} más)`;
}

function resolveSafetyStockSelectionSummary(newValue: Record<string, any> | undefined): string {
  const details = newValue?.selectedItemsDetail;
  if (Array.isArray(details) && details.length > 0) {
    return formatSafetyStockSelectionItemsSummary(details as SafetyStockSelectionItemDetail[]);
  }
  const scope = newValue?.scopeDescription;
  if (typeof scope === 'string' && scope.trim()) {
    return scope.trim();
  }
  const productSummary = newValue?.productSummary;
  if (typeof productSummary === 'string' && productSummary.trim()) {
    return productSummary.trim();
  }
  return '';
}

function generateSafetyStockBulkMessage(
  newValue: Record<string, any> | undefined,
  notes: NotesData | undefined,
): string | undefined {
  if (!newValue && !notes?.metadata) return undefined;
  const source = newValue?.source ?? notes?.metadata?.source;
  const val = newValue?.safetyStock;
  const n = newValue?.recordsUpdated ?? notes?.metadata?.recordsUpdated;
  const valStr = val == null ? '—' : String(val);
  const countStr = n != null ? `${n} registro(s)` : 'varios registros';

  if (source === 'BULK_SELECTION') {
    const productsSummary = resolveSafetyStockSelectionSummary(newValue);
    const productsPart = productsSummary ? `${productsSummary}. ` : '';
    return `Stock de seguridad (actualización por selección): ${productsPart}Nuevo valor: ${valStr}. Actualizados: ${countStr}.`;
  }

  const scope = newValue?.scopeDescription ?? notes?.metadata?.scopeDescription;
  const scopeStr = scope ? `${scope}. ` : '';
  return `Stock de seguridad (edición personalizada): ${scopeStr}Nuevo valor: ${valStr}. Actualizados: ${countStr}.`;
}

function generatePhysicalCountFinalizedMessage(
  newValue: Record<string, any> | undefined,
): string | undefined {
  if (!newValue) return undefined;
  const sessionCode = newValue.sessionCode;
  const expected = newValue.expectedCount;
  const actual = newValue.actualCount;
  const missing = newValue.missingCount;
  const excess = newValue.excessCount;
  const duplicates = newValue.duplicateCount;
  const notAvailable = newValue.scannedNotAvailableCount;
  const code = sessionCode ? ` ${sessionCode}` : '';
  return `Conteo fisico finalizado${code}: esperados ${expected ?? 0}, contados ${actual ?? 0}, faltantes ${missing ?? 0}, sobrantes ${excess ?? 0}, duplicados ${duplicates ?? 0}, no disponibles ${notAvailable ?? 0}.`;
}

function generateRoleCreatedMessage(newValue: Record<string, any> | undefined): string | undefined {
  if (!newValue) return undefined;
  const roleName = newValue.name;
  const description = newValue.description;
  const permissionsCount = newValue.permissionsCount;
  if (!roleName) return 'Rol creado';
  const descriptionPart = description ? ` Descripción: ${description}.` : '';
  const permissionsPart =
    typeof permissionsCount === 'number' ? ` Permisos asignados: ${permissionsCount}.` : '';
  return `Rol creado: ${roleName}.${descriptionPart}${permissionsPart}`.trim();
}

function generateRoleUpdatedMessage(
  previousValue: Record<string, any> | undefined,
  newValue: Record<string, any> | undefined,
): string | undefined {
  const previousName = previousValue?.name;
  const newName = newValue?.name;
  const previousDescription = previousValue?.description;
  const newDescription = newValue?.description;

  const roleLabel = newName || previousName || 'Rol';
  const changes: string[] = [];

  if (previousName !== undefined && newName !== undefined && previousName !== newName) {
    changes.push(`nombre: ${previousName} -> ${newName}`);
  }

  if (
    previousDescription !== undefined &&
    newDescription !== undefined &&
    previousDescription !== newDescription
  ) {
    const prevDesc = previousDescription || 'sin descripción';
    const nextDesc = newDescription || 'sin descripción';
    changes.push(`descripción: ${prevDesc} -> ${nextDesc}`);
  }

  return changes.length > 0
    ? `Rol actualizado: ${roleLabel}. Cambios: ${changes.join(', ')}.`
    : `Rol actualizado: ${roleLabel}.`;
}

function generateRoleDeletedMessage(
  previousValue: Record<string, any> | undefined,
): string | undefined {
  const roleName = previousValue?.name;
  return roleName ? `Rol eliminado: ${roleName}.` : 'Rol eliminado.';
}

function generateRolePermissionsUpdatedMessage(
  newValue: Record<string, any> | undefined,
): string | undefined {
  if (!newValue) return undefined;
  const roleName = newValue.roleName;
  const addedCount = newValue.addedCount ?? 0;
  const removedCount = newValue.removedCount ?? 0;
  const totalPermissions = newValue.totalPermissions;
  const roleText = roleName ? ` para ${roleName}` : '';
  const totalText =
    typeof totalPermissions === 'number' ? ` Permisos totales actuales: ${totalPermissions}.` : '';
  return `Permisos de rol actualizados${roleText}: agregados ${addedCount}, removidos ${removedCount}.${totalText}`;
}

function generateUserCreatedMessage(newValue: Record<string, any> | undefined): string | undefined {
  if (!newValue) return undefined;
  const email = newValue.email;
  const firstName = newValue.firstName;
  const lastName = newValue.lastName;
  const roleNames = Array.isArray(newValue.roleNames) ? newValue.roleNames : [];
  const userLabel =
    firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '';
  const identity = userLabel ? `${userLabel} (${email || 'sin email'})` : email || 'usuario';
  const rolesText = roleNames.length > 0 ? ` Roles: ${roleNames.join(', ')}.` : '';
  return `Usuario creado: ${identity}.${rolesText}`;
}

function generateUserUpdatedMessage(
  previousValue: Record<string, any> | undefined,
  newValue: Record<string, any> | undefined,
): string | undefined {
  const email = newValue?.email ?? previousValue?.email;
  const changed: string[] = [];

  const comparableFields: Array<{ key: string; label: string }> = [
    { key: 'firstName', label: 'nombre' },
    { key: 'lastName', label: 'apellido' },
    { key: 'email', label: 'email' },
    { key: 'isActive', label: 'estado' },
    { key: 'primaryLocationId', label: 'sucursal principal' },
    {
      key: 'canSellFromOtherLocations',
      label: 'ventas multi-sucursal (venta y órdenes)',
    },
  ];

  comparableFields.forEach(({ key, label }) => {
    if (previousValue?.[key] !== newValue?.[key]) {
      changed.push(label);
    }
  });

  if (newValue?.passwordUpdated) {
    changed.push('contraseña');
  }

  const changedText = changed.length > 0 ? ` Cambios: ${changed.join(', ')}.` : '';
  return `Usuario actualizado${email ? ` (${email})` : ''}.${changedText}`;
}

function generateUserDeletedMessage(
  previousValue: Record<string, any> | undefined,
): string | undefined {
  const email = previousValue?.email;
  const firstName = previousValue?.firstName;
  const lastName = previousValue?.lastName;
  const userLabel =
    firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '';
  const identity = userLabel ? `${userLabel} (${email || 'sin email'})` : email || 'usuario';
  return `Usuario eliminado: ${identity}.`;
}

function generateUserRolesUpdatedMessage(
  newValue: Record<string, any> | undefined,
): string | undefined {
  if (!newValue) return undefined;
  const email = newValue.email;
  const addedCount = newValue.addedCount ?? 0;
  const removedCount = newValue.removedCount ?? 0;
  const addedRoleNames = Array.isArray(newValue.addedRoleNames) ? newValue.addedRoleNames : [];
  const removedRoleNames = Array.isArray(newValue.removedRoleNames)
    ? newValue.removedRoleNames
    : [];
  const addedText = addedRoleNames.length > 0 ? ` Agregados: ${addedRoleNames.join(', ')}.` : '';
  const removedText =
    removedRoleNames.length > 0 ? ` Removidos: ${removedRoleNames.join(', ')}.` : '';
  return `Roles de usuario actualizados${email ? ` (${email})` : ''}: +${addedCount}, -${removedCount}.${addedText}${removedText}`;
}

// Account security message generators
function generateLoginSuccessMessage(): string {
  return 'Inicio de sesión exitoso';
}

function generateLoginFailedMessage(newValue: Record<string, any> | undefined): string {
  const attempt = newValue?.attemptNumber;
  return typeof attempt === 'number' && attempt > 0
    ? `Inicio de sesión fallido (intento ${attempt})`
    : 'Inicio de sesión fallido';
}

function generateAccountLockedMessage(newValue: Record<string, any> | undefined): string {
  const reason = typeof newValue?.reason === 'string' ? newValue.reason.trim() : '';
  return reason ? `Cuenta bloqueada: ${reason}` : 'Cuenta bloqueada';
}

function generateAccountUnlockedMessage(): string {
  return 'Cuenta desbloqueada';
}

function generatePasswordChangedMessage(): string {
  return 'Contraseña cambiada';
}

function generatePasswordResetByAdminMessage(newValue: Record<string, any> | undefined): string {
  const expiresAt = typeof newValue?.expiresAt === 'string' ? newValue.expiresAt.trim() : '';
  return expiresAt
    ? `Contraseña restablecida por admin (temporal válida hasta ${expiresAt})`
    : 'Contraseña restablecida por admin';
}

function generateForcedPasswordChangeMessage(): string {
  return 'Cambio de contraseña obligatorio completado';
}

// Reservation message generators
function generateReservationExpiredMessage(
  newValue: Record<string, any> | undefined,
  notes: NotesData | undefined,
): string | undefined {
  // Check if auto-expired from metadata
  if (notes?.metadata?.autoExpired) {
    return 'Reserva expirada automáticamente';
  }

  // Check if reserved_by is null (expired)
  if (newValue?.reserved_by === null || newValue?.reserved_by === undefined) {
    return 'Reserva expirada';
  }

  return undefined;
}
